import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CryptoExchangeUser } from "./CryptoExchange";
import ClientGameTopup from "./ClientGameTopup";
import { BalanceWidget, AffiliateSelector } from "./BalanceAndAffiliates";
import { Sidebar } from "./Sidebar";
import NotificationCenter from "./NotificationCenter";
import { useExchangeRate, formatUSD, formatHTG, htgToUsd } from "./lib/currency";
import { uploadPaymentProof } from "./lib/uploadProof";
import ClientBalanceView from "./ClientBalanceView";
import P2PMarketplace from "./P2PMarketplace";
import RecipientInfo from "./RecipientInfo";
import PromoVideoPopup from "./PromoVideoPopup";
import ClientChatWidget from "./ClientChatWidget";
import ClientWhatsAppSecondary from "./ClientWhatsAppSecondary";
import ClientSMM from "./ClientSMM";
import {
  LogOut, Clock, CheckCircle, XCircle, History, ShoppingCart,
  Tv, Play, Gift, TrendingUp, CreditCard, Gamepad2, Phone, Megaphone,
  ChevronRight, ChevronLeft, Search, DollarSign, Bell, Lock,
  Tag, UserCheck, Percent, BookOpen, Video, FileText, Eye,
  Award, Download, Star, MessageCircle, Send, User, CalendarDays, Timer,
  Upload, Image, Copy, Bitcoin, Wallet, QrCode, ArrowRightLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === 'smartechsystempro.com' || hostname === 'www.smartechsystempro.com') return `${protocol}//${hostname}/api`;
  if (hostname.includes('emergent.host') || hostname.includes('emergentagent.com')) return `${protocol}//${hostname}/api`;
  return BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
};
const API = getApiUrl();

const apiCall = async (method, url, data = null) => {
  const token = localStorage.getItem("client_token");
  const config = { method, url: `${API}${url}`, headers: token ? { Authorization: `Bearer ${token}` } : {}, data };
  return axios(config);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const SERVICE_ICONS = {
  tv: Tv, play: Play, gift: Gift, trending: TrendingUp,
  bitcoin: DollarSign, "credit-card": CreditCard, gamepad: Gamepad2,
  exchange: CreditCard
};

const PAYMENT_PHONE = "+50946261180";

const QR_CODES = [
  {
    image: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/7b1bzifc_IMG-20260320-WA0055%282%29.jpg",
    label: "MonCash",
    number: "46261180",
    name: "Pointdujour Frenson"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/6vevjn52_IMG-20260320-WA0054%282%29.jpg",
    label: "Digicel",
    number: "50955460588",
    name: "FRENSON POINT-DU-JOUR"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/59klzhb1_IMG-20260320-WA0053%283%29.jpg",
    label: "MonCash QR",
    number: "+50947096761",
    name: "Pointdujour Frenson"
  }
];

const ClientDashboard = ({ clientUser, onLogout, initialView }) => {
  const [activeView, setActiveView] = useState(initialView || "services");
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [customAmountUsd, setCustomAmountUsd] = useState("");
  // Generic recipient info text (Binance UID, wallet, email, free-text…) — set by client when admin's service
  // is tagged requires_recipient_info with a generic kind.
  const [recipientInfo, setRecipientInfo] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("qr");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientHistory, setClientHistory] = useState([]);
  // New order fields
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState("");
  const [profileName, setProfileName] = useState("");
  const [orderDurationDays, setOrderDurationDays] = useState("");
  // Affiliate code & discount
  const [affiliateCodeInput, setAffiliateCodeInput] = useState("");
  const [activeDiscount, setActiveDiscount] = useState(null);
  const [settingCode, setSettingCode] = useState(false);
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  // Courses
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseTransactionId, setCourseTransactionId] = useState("");
  const [coursePaymentMethod, setCoursePaymentMethod] = useState("qr");
  const [buyingCourse, setBuyingCourse] = useState(false);
  const [viewingCourse, setViewingCourse] = useState(null);
  // Certificates
  const [myCertificates, setMyCertificates] = useState([]);
  const [certNotifications, setCertNotifications] = useState([]);
  // Subscriptions
  const [mySubscriptions, setMySubscriptions] = useState([]);
  // Explainer video
  const [clientVideo, setClientVideo] = useState(null);
  // Notification email
  const [notifEmail, setNotifEmail] = useState("");
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  // WhatsApp setup banner (welcome message)
  const [waInput, setWaInput] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  const [waBannerDismissed, setWaBannerDismissed] = useState(false);
  // Recipient info for services that require it
  const [recipientType, setRecipientType] = useState("email"); // "email" | "crypto" | "wise" | "freefire" | "giftcard"
  const [recEmail, setRecEmail] = useState("");
  const [recBenefName, setRecBenefName] = useState("");
  const [recCrypto, setRecCrypto] = useState("");
  const [recNetwork, setRecNetwork] = useState("");
  const [recAddress, setRecAddress] = useState("");
  const [recLink, setRecLink] = useState("");
  const [recWiseTag, setRecWiseTag] = useState("");
  // Free Fire / game top-up
  const [recPlayerName, setRecPlayerName] = useState("");
  const [recGameId, setRecGameId] = useState("");
  const [recGamePassword, setRecGamePassword] = useState(""); // for eFootball etc.
  // Boost (Facebook/TikTok/Instagram)
  const [recBoostPlatform, setRecBoostPlatform] = useState("facebook");
  const [recBoostPostLink, setRecBoostPostLink] = useState("");
  const [recBoostBudget, setRecBoostBudget] = useState("1");
  const [recBoostDays, setRecBoostDays] = useState("1");
  // Live exchange rate (HTG per USD)
  const exchangeRate = useExchangeRate();
  // Client balance (in HTG) — fetched once for the "insufficient balance" UX
  const [clientBalanceHtg, setClientBalanceHtg] = useState(0);
  // Self-service password change
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  // Cashback
  const [cashbackInfo, setCashbackInfo] = useState(null);

  const fetchData = useCallback(async () => {
    try { const r = await apiCall("get", "/services"); setServices(r.data); } catch {}
    try { const r = await apiCall("get", "/client/orders"); setOrders(r.data); } catch {}
    try { const r = await apiCall("get", "/client/stats"); setStats(r.data); } catch {}
    try { const r = await apiCall("get", "/client/history"); setClientHistory(r.data); } catch {}
    try { const r = await apiCall("get", "/client/active-discount"); setActiveDiscount(r.data); } catch {}
    try { const r = await apiCall("get", "/client/notifications"); setNotifications(r.data); } catch {}
    try { const r = await apiCall("get", "/client/notifications/unread-count"); setUnreadCount(r.data.count); } catch {}
    try { const r = await apiCall("get", "/public/courses"); setCourses(r.data); } catch {}
    try { const r = await apiCall("get", "/client/my-courses"); setMyCourses(r.data); } catch {}
    try { const r = await apiCall("get", "/client/certificates"); setMyCertificates(r.data); } catch {}
    try { const r = await apiCall("get", "/client/certificate-notifications"); setCertNotifications(r.data); } catch {}
    try { const r = await apiCall("get", "/client/subscriptions"); setMySubscriptions(r.data); } catch {}
    try { const r = await axios.get(`${API}/public/explainer-video/client`); if (r.data.video_url) setClientVideo(r.data); } catch {}
    try { const r = await apiCall("get", "/client/stats"); if (!r.data.notification_email) setShowEmailPrompt(true); else setShowEmailPrompt(false); } catch {}
    try { const r = await apiCall("get", "/client/cashback-info"); setCashbackInfo(r.data); } catch {}
    try { const r = await apiCall("get", "/balance"); setClientBalanceHtg(Number(r.data?.balance) || 0); } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Bug fix 2026-06-06: when a logged-in client arrives from the StoreFront via
  // `/client?service_id=…&plan_id=…`, auto-open the right service/plan in the
  // dashboard (instead of forcing them to navigate manually). The legacy
  // StoreFront quick-buy modal (transaction_id + WhatsApp) is now unreachable
  // for authenticated clients.
  useEffect(() => {
    if (!services || services.length === 0) return;
    try {
      const sp = new URLSearchParams(window.location.search);
      const sid = sp.get("service_id");
      const pid = sp.get("plan_id");
      if (!sid) return;
      const svc = services.find(s => s.id === sid || s.service_id === sid);
      if (!svc) return;
      const plan = pid ? (svc.plans || []).find(p => p.id === pid) : (svc.plans || [])[0];
      if (!plan) return;
      // Defer so dependent state is in place.
      setTimeout(() => {
        try { handleSelectPlan(svc, plan); } catch { /* noop */ }
      }, 50);
      // Clean the URL so a page refresh doesn't replay the popup.
      try {
        const cleaned = window.location.pathname;
        window.history.replaceState(null, "", cleaned);
      } catch { /* noop */ }
    } catch { /* noop */ }
    // We deliberately run this only when `services` becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  // When focus returns, refresh balance. If it increased since the last
  // snapshot, surprise the user with a toast — no setInterval polling.
  useEffect(() => {
    const refreshBalance = async () => {
      try {
        const r = await apiCall("get", "/balance");
        const next = Number(r.data?.balance) || 0;
        setClientBalanceHtg(prev => {
          if (next > prev + 0.01 && prev >= 0) {
            const diffHtg = next - prev;
            const rate = exchangeRate || 140;
            const diffUsd = diffHtg / rate;
            toast.success(
              `💰 +${diffUsd.toFixed(2)} $ — Solde mis à jour : ${(next / rate).toFixed(2)} $`,
              { duration: 7000 }
            );
          }
          return next;
        });
      } catch { /* silent */ }
    };
    const onFocus = () => refreshBalance();
    const onVis = () => { if (document.visibilityState === "visible") refreshBalance(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [exchangeRate]);

  // Handle ?thanks=1 redirect from affiliate WhatsApp link — show thank-you message
  // and land directly on the dashboard view.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("thanks") === "1") {
        setActiveView("orders");
        setTimeout(() => {
          toast.success(
            "Merci d'avoir utilisé SMARTECH SYSTEM PRO ! Votre commande a été traitée avec succès.",
            { duration: 6000 }
          );
        }, 300);
        // Clean URL so the toast doesn't fire again on every render / reload
        const url = new URL(window.location.href);
        url.searchParams.delete("thanks");
        url.searchParams.delete("order");
        window.history.replaceState({}, "", url.toString());
      }
    } catch {}
  }, []);

  const handleSetAffiliateCode = async () => {
    if (!affiliateCodeInput.trim()) { toast.error("Entrez un code affilié"); return; }
    setSettingCode(true);
    try {
      const res = await apiCall("post", "/client/set-affiliate-code", { affiliate_code: affiliateCodeInput.trim() });
      toast.success(res.data.message);
      setAffiliateCodeInput("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSettingCode(false);
    }
  };

  const handleSelectPlan = async (service, plan) => {
    setSelectedService(service);
    setSelectedPlan(plan);
    setTransactionId("");
    setCustomAmountUsd("");
    // Force balance payment for services that require recipient info (Meru/Fyatu/Wise/PayPal)
    setPaymentMethod(service?.balance_payment_only ? "balance" : "qr");
    setPaymentProofFile(null);
    setPaymentProofUrl("");
    setProfileName("");
    setOrderDurationDays("");
    // Reset recipient info
    setRecipientType(service?.recipient_kind === "wise" ? "wise" : "email");
    setRecEmail(""); setRecBenefName(""); setRecCrypto(""); setRecNetwork("");
    setRecipientInfo("");
    setRecAddress(""); setRecLink(""); setRecWiseTag("");
    setRecPlayerName(""); setRecGameId("");
    setShowPayment(true);
    // Refresh balance so the order page never shows a stale "Solde actuel = 0"
    // immediately after a recharge. Runs in parallel with modal opening.
    try {
      const r = await apiCall("get", "/balance");
      setClientBalanceHtg(Number(r.data?.balance) || 0);
    } catch { /* silent */ }
  };

  const calculatePrice = () => {
    if (!selectedPlan) return 0;
    let base = selectedPlan.price;
    // Boost service: dynamic price = base_price × budget × days
    if (selectedService?.recipient_kind === "boost") {
      const budget = parseFloat(recBoostBudget) || 0;
      const days = parseInt(recBoostDays) || 0;
      const basePrice = parseFloat(selectedService?.boost_base_price_htg || 200);
      return +(basePrice * budget * days).toFixed(2);
    }
    if (selectedPlan.price > 0) {
      // Fixed price
      base = selectedPlan.price;
    } else if (selectedPlan.is_exchange && customAmountUsd) {
      const amount = parseFloat(customAmountUsd);
      const feeTable = selectedService?.fee_table || [];
      for (const tier of feeTable) {
        if (amount >= tier.min && amount <= tier.max) {
          const fee = selectedPlan.exchange_type === "moncash" ? tier.moncash_fee : tier.natcash_fee;
          return amount + fee; // No discount on exchange
        }
      }
      return amount;
    } else if (selectedPlan.rate && customAmountUsd) {
      base = parseFloat(customAmountUsd) * selectedPlan.rate;
    } else {
      return 0;
    }
    // Apply service-specific extra fee (Meru/Fyatu 10%, Wise/PayPal 15%)
    const feePct = Number(selectedService?.client_fee_percent) || 0;
    if (feePct > 0) {
      base = base * (1 + feePct / 100);
    }
    return base;
  };

  const getDiscountedPrice = () => {
    const base = calculatePrice();
    if (!activeDiscount || activeDiscount.discount <= 0) return base;
    if (selectedPlan?.is_exchange) return base; // No discount on exchange
    const discountAmount = Math.round(base * activeDiscount.discount / 100 * 100) / 100;
    return Math.round((base - discountAmount) * 100) / 100;
  };

  const getExchangeFee = () => {
    if (!selectedPlan?.is_exchange || !customAmountUsd) return 0;
    const amount = parseFloat(customAmountUsd);
    const feeTable = selectedService?.fee_table || [];
    for (const tier of feeTable) {
      if (amount >= tier.min && amount <= tier.max) {
        return selectedPlan.exchange_type === "moncash" ? tier.moncash_fee : tier.natcash_fee;
      }
    }
    return 0;
  };

  const handleSubmitOrder = async () => {
    // Build client_payload for services that require recipient info
    let clientPayload = null;
    if (selectedService?.requires_recipient_info) {
      const kind = selectedService.recipient_kind;
      if (kind === "wise") {
        if (!recEmail.trim()) { toast.error("Email Wise requis"); return; }
        if (!recBenefName.trim()) { toast.error("Nom du bénéficiaire requis"); return; }
        if (!recWiseTag.trim() || !recWiseTag.startsWith("@")) { toast.error("Wise Tag (commençant par @) requis"); return; }
        clientPayload = {
          type: "wise",
          email: recEmail.trim(),
          beneficiary_name: recBenefName.trim(),
          wise_tag: recWiseTag.trim(),
        };
      } else if (kind === "freefire" || kind === "efootball") {
        if (!recPlayerName.trim()) { toast.error("Nom du joueur requis"); return; }
        if (!recGameId.trim()) { toast.error("ID du compte requis"); return; }
        if (kind === "efootball" && !recGamePassword.trim()) {
          toast.error("Mot de passe du compte eFootball requis"); return;
        }
        clientPayload = {
          type: kind,
          player_name: recPlayerName.trim(),
          game_id: recGameId.trim(),
        };
        if (recGamePassword.trim()) clientPayload.password = recGamePassword.trim();
      } else if (kind === "boost") {
        if (!["facebook", "tiktok", "instagram"].includes(recBoostPlatform)) {
          toast.error("Plateforme requise"); return;
        }
        const link = recBoostPostLink.trim();
        if (!link || !(link.startsWith("http://") || link.startsWith("https://"))) {
          toast.error("Lien du post valide requis (https://...)"); return;
        }
        const budget = parseFloat(recBoostBudget);
        const days = parseInt(recBoostDays);
        if (!budget || budget < 1 || budget > 100) { toast.error("Budget par jour invalide (1-100$)"); return; }
        if (!days || days < 1 || days > 90) { toast.error("Durée invalide (1-90 jours)"); return; }
        const basePrice = parseFloat(selectedService?.boost_base_price_htg || 200);
        const total = +(basePrice * budget * days).toFixed(2);
        clientPayload = {
          type: "boost",
          platform: recBoostPlatform,
          post_link: link,
          budget_usd_per_day: budget,
          days,
          base_price_htg: basePrice,
          computed_price_htg: total,
        };
      } else if (kind === "giftcard_email") {
        const em = recEmail.trim();
        if (!em || !em.includes("@")) { toast.error("Adresse email valide requise"); return; }
        clientPayload = { type: "giftcard_email", email: em };
      } else if (["free_text", "email", "wallet", "account_id"].includes(kind)) {
        // Generic recipient info — Bitcoin / trading / credit-card / exchange services
        const val = (recipientInfo || "").trim();
        if (!val) {
          toast.error(
            kind === "email"   ? "Email destinataire requis" :
            kind === "wallet"  ? "Adresse de wallet requise" :
            kind === "account_id" ? "Identifiant du compte requis" :
            "Détails pour l'administrateur requis"
          );
          return;
        }
        if (kind === "email" && !val.includes("@")) {
          toast.error("Adresse email valide requise"); return;
        }
        clientPayload = { type: kind, value: val };
      } else if (recipientType === "email") {
        if (!recEmail.trim()) { toast.error("Email du compte requis"); return; }
        if (!recBenefName.trim()) { toast.error("Nom du bénéficiaire requis"); return; }
        clientPayload = {
          type: "email",
          email: recEmail.trim(),
          beneficiary_name: recBenefName.trim(),
        };
      } else {
        if (!recCrypto.trim()) { toast.error("Type de crypto requis"); return; }
        if (!recNetwork.trim()) { toast.error("Réseau requis"); return; }
        if (!recAddress.trim()) { toast.error("Adresse crypto requise"); return; }
        clientPayload = {
          type: "crypto",
          crypto: recCrypto.trim(),
          network: recNetwork.trim(),
          address: recAddress.trim(),
          link: recLink.trim() || undefined,
        };
      }
    }

    // Proof required UNLESS paying with internal balance
    if (paymentMethod !== "balance" && !paymentProofUrl) {
      toast.error("Téléversez une preuve de paiement"); return;
    }
    if (selectedPlan.is_exchange) {
      if (!customAmountUsd || parseFloat(customAmountUsd) < 250) {
        toast.error("Montant minimum: 250 Gdes"); return;
      }
      if (parseFloat(customAmountUsd) > 99999) {
        toast.error("Montant maximum: 99,999 Gdes"); return;
      }
    } else if (selectedPlan.price === 0 && (!customAmountUsd || parseFloat(customAmountUsd) <= 0)) {
      toast.error("Entrez un montant valide"); return;
    }
    if (selectedPlan.min_usd && parseFloat(customAmountUsd) < selectedPlan.min_usd) {
      toast.error(`Montant minimum: ${selectedPlan.min_usd}$`); return;
    }

    // Insufficient balance UX (only when paying with balance)
    if (paymentMethod === "balance") {
      const finalPriceHtg = getDiscountedPrice();
      if (finalPriceHtg > clientBalanceHtg) {
        const missingUsd = htgToUsd(finalPriceHtg - clientBalanceHtg, exchangeRate).toFixed(2);
        toast.error(`Solde insuffisant — il manque ${missingUsd} $. Rechargez votre solde.`);
        // Redirect to "Mon solde"
        setShowPayment(false);
        setActiveView("balance");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const res = await apiCall("post", "/client/order", {
        plan_id: selectedPlan.id,
        custom_amount_usd: customAmountUsd ? parseFloat(customAmountUsd) : null,
        payment_method: paymentMethod,
        payment_proof_url: paymentProofUrl || null,
        profile_name: selectedService?.requires_extras ? (profileName.trim() || null) : null,
        duration_days: selectedService?.requires_extras && orderDurationDays ? parseInt(orderDurationDays) : null,
        client_payload: clientPayload || undefined,
      });

      if (paymentMethod === "balance") {
        toast.success("Commande enregistrée — contactez l'admin sur WhatsApp pour un traitement rapide");
      } else {
        toast.success("Commande envoyée! En attente de validation.");
      }
      // For BALANCE payments, open admin WhatsApp DIRECT with time-stamped reminder
      if (paymentMethod === "balance") {
        try {
          const now = new Date();
          const eta = new Date(now.getTime() + 10 * 60 * 1000);
          const pad = (n) => String(n).padStart(2, "0");
          const fmt = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
          const msg =
            `Bonjour Admin, je viens de passer une commande. Merci de vérifier pour moi s'il vous plaît. ` +
            `Je sais que cela prend seulement 5 à 10 minutes.\n\n` +
            `• Transaction effectuée à ${fmt(now)}\n` +
            `• Heure estimée de traitement : ${fmt(eta)}`;
          const url = "https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2";
          try { await navigator.clipboard.writeText(msg); } catch {}
          window.open(url, "_blank", "noopener");
        } catch (e) { /* ignore */ }
      } else if (res.data.notify_admin_wa && paymentMethod !== "balance") {
        // For QR payments, notify admin with generic order info
        window.open(res.data.notify_admin_wa, "_blank");
      }
      setShowPayment(false);
      setSelectedService(null);
      setSelectedPlan(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPaymentProofFile(file);
    try {
      const data = await uploadPaymentProof(file, { role: "client", tokenKey: "client_token" });
      setPaymentProofUrl(data.url);
      toast.success("Preuve téléversée!");
    } catch (err) {
      toast.error(err.message || "Erreur de téléversement", { duration: 6000 });
      setPaymentProofFile(null);
    } finally {
      // Reset input so re-selecting the same file works after an error.
      try { e.target.value = ""; } catch (_) { /* noop */ }
    }
  };

  const filteredServices = services.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.plans.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handlePurchaseCourse = async () => {
    if (!courseTransactionId.trim()) { toast.error("ID de transaction requis"); return; }
    setBuyingCourse(true);
    try {
      await apiCall("post", "/client/purchase-course", {
        course_id: selectedCourse.id,
        transaction_id: courseTransactionId.trim(),
        payment_method: coursePaymentMethod
      });
      toast.success("Achat envoye! En attente de validation.");
      setSelectedCourse(null);
      setCourseTransactionId("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBuyingCourse(false);
    }
  };

  const isCoursePurchased = (courseId) => {
    return myCourses.find(c => c.id === courseId);
  };

  // Client messaging with professor
  const [chatProfId, setChatProfId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState("");

  const openChat = async (professorId, courseId) => {
    setChatProfId(professorId);
    try {
      const res = await apiCall("get", `/client/messages/${professorId}`);
      setChatMessages(res.data);
    } catch {}
  };

  const sendChatMessage = async () => {
    if (!chatText.trim() || !chatProfId) return;
    try {
      await apiCall("post", `/client/messages/${chatProfId}`, { content: chatText });
      setChatText("");
      const res = await apiCall("get", `/client/messages/${chatProfId}`);
      setChatMessages(res.data);
    } catch { toast.error("Erreur envoi"); }
  };

  const handleRequestCertificate = async (courseId) => {
    try {
      await apiCall("post", "/client/request-certificate", { course_id: courseId });
      toast.success("Demande de certificat envoyee au professeur!");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleSubmitReview = async (courseId, rating, comment) => {
    try {
      await apiCall("post", "/client/review", { course_id: courseId, rating, comment });
      toast.success("Avis envoye!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const getCertForCourse = (courseId) => {
    return myCertificates.find(c => c.course_id === courseId && c.status === "completed");
  };

  const getStatusBadge = (status) => {
    if (status === "validated") return <span className="order-status validated"><CheckCircle size={12} /> Validé</span>;
    if (status === "rejected") return <span className="order-status rejected"><XCircle size={12} /> Rejeté</span>;
    return <span className="order-status pending"><Clock size={12} /> En attente</span>;
  };

  const ServiceIcon = ({ icon }) => {
    const IconComp = SERVICE_ICONS[icon] || ShoppingCart;
    return <IconComp size={20} />;
  };

  // Payment modal
  if (showPayment && selectedService && selectedPlan) {
    const price = calculatePrice();
    const finalPrice = getDiscountedPrice();
    const hasDiscount = activeDiscount && activeDiscount.discount > 0 && !selectedPlan.is_exchange && price !== finalPrice;
    return (
      <div className="client-dashboard" data-testid="client-dashboard">
        <header className="client-header">
          <button onClick={() => setShowPayment(false)} className="back-btn" data-testid="back-btn">
            <ChevronLeft size={20} /> Retour
          </button>
          <h1 className="header-title-sm">Paiement</h1>
          <div style={{ width: 40 }} />
        </header>
        <div className="client-content">
          <div className="payment-card" data-testid="payment-card">
            <div className="payment-service-info">
              <ServiceIcon icon={selectedService.icon} />
              <div>
                <div className="payment-service-name">{selectedService.name}</div>
                <div className="payment-plan-name">{selectedPlan.name}</div>
              </div>
            </div>

            {((selectedPlan.price === 0 && selectedPlan.rate) || (selectedService?.is_custom && selectedPlan.rate)) && !selectedPlan.is_exchange && (
              <div className="custom-amount-section">
                <label className="form-label">Montant en USD {selectedPlan.min_usd ? `(min ${selectedPlan.min_usd}$)` : ""}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  placeholder="Saisissez le montant souhaité"
                  value={customAmountUsd}
                  onChange={e => setCustomAmountUsd(e.target.value)}
                  min={selectedPlan.min_usd || 0}
                  data-testid="custom-amount-input"
                />
                <div className="rate-info" style={{ display: "none" }}>Taux interne</div>
              </div>
            )}

            {/* Generic recipient-info input for services that admin tagged with requires_recipient_info */}
            {selectedService?.requires_recipient_info && ["free_text", "email", "wallet", "account_id"].includes(selectedService?.recipient_kind) && (
              <div className="custom-amount-section" data-testid="generic-recipient-section">
                <label className="form-label">
                  {selectedService.recipient_kind === "email" && "Email du destinataire *"}
                  {selectedService.recipient_kind === "wallet" && "Adresse de wallet crypto *"}
                  {selectedService.recipient_kind === "account_id" && "Identifiant du compte (ex: Binance UID, ID broker) *"}
                  {selectedService.recipient_kind === "free_text" && "Détails pour l'administrateur *"}
                </label>
                {selectedService.recipient_kind === "free_text" ? (
                  <textarea
                    className="form-input"
                    rows={3}
                    placeholder="Décrivez ce que vous voulez (compte, opération, lien, etc.)"
                    value={customAmountUsd && customAmountUsd.__isPayload ? "" : (recipientInfo || "")}
                    onChange={e => setRecipientInfo(e.target.value)}
                    data-testid="generic-recipient-input"
                  />
                ) : (
                  <input
                    type={selectedService.recipient_kind === "email" ? "email" : "text"}
                    className="form-input"
                    placeholder={
                      selectedService.recipient_kind === "email" ? "exemple@email.com" :
                      selectedService.recipient_kind === "wallet" ? "Adresse wallet (BTC/USDT/...)" :
                      "Votre identifiant"
                    }
                    value={recipientInfo || ""}
                    onChange={e => setRecipientInfo(e.target.value)}
                    data-testid="generic-recipient-input"
                  />
                )}
                <div style={{ fontSize: "0.7rem", color: "#64748B", marginTop: 4 }}>
                  Ces informations seront transmises à l'administrateur pour traiter votre commande.
                </div>
              </div>
            )}

            {selectedPlan.is_exchange && (
              <div className="exchange-section">
                <label className="form-label">Montant en Gdes (min 250)</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Montant en Gdes"
                  value={customAmountUsd}
                  onChange={e => setCustomAmountUsd(e.target.value)}
                  min={250}
                  data-testid="exchange-amount-input"
                />
                {customAmountUsd && parseFloat(customAmountUsd) >= 250 && (
                  <div className="exchange-breakdown">
                    <div className="exchange-row">
                      <span>Montant:</span>
                      <span>{formatUSD(parseFloat(customAmountUsd) || 0, exchangeRate)}</span>
                    </div>
                    <div className="exchange-row fee-row">
                      <span>Frais {selectedPlan.exchange_type === "moncash" ? "MonCash" : "NatCash"}:</span>
                      <span>+{formatUSD(getExchangeFee() || 0, exchangeRate)}</span>
                    </div>
                    <div className="exchange-row total-row">
                      <span>Total:</span>
                    <strong>{formatUSD(calculatePrice(), exchangeRate)}</strong>
                  </div>
                </div>
                )}
                {/* Fee table */}
                <details className="fee-table-details">
                  <summary>Voir la grille tarifaire</summary>
                  <table className="fee-table" data-testid="fee-table">
                    <thead>
                      <tr>
                        <th>Montant</th>
                        <th>Frais MC</th>
                        <th>Total MC</th>
                        <th>Frais NC</th>
                        <th>Total NC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedService?.fee_table || []).map((tier, i) => (
                        <tr key={`tier-${tier.min}-${tier.max}-${i}`} className={customAmountUsd && parseFloat(customAmountUsd) >= tier.min && parseFloat(customAmountUsd) <= tier.max ? "active-tier" : ""}>
                          <td>{tier.min.toLocaleString()}-{tier.max.toLocaleString()}</td>
                          <td>{tier.moncash_fee}</td>
                          <td>{(tier.min + tier.moncash_fee).toLocaleString()}</td>
                          <td>{tier.natcash_fee}</td>
                          <td>{(tier.min + tier.natcash_fee).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </div>
            )}

            {!selectedPlan.is_exchange && (
              <div className="payment-total-section">
                {hasDiscount && (
                  <div className="discount-badge" data-testid="discount-badge">
                    <Tag size={14} /> -{activeDiscount.discount}% bonus (usage unique)
                  </div>
                )}
                {selectedService?.client_fee_percent > 0 && (
                  <div style={{ background: "#FEF3C7", color: "#92400E", padding: "0.45rem 0.6rem", borderRadius: 8, fontSize: "0.78rem", marginBottom: "0.4rem", border: "1px solid #FDE68A" }} data-testid="service-fee-notice">
                    Frais de service : <strong>+{selectedService.client_fee_percent}%</strong> (inclus dans le total)
                  </div>
                )}
                <div className="payment-total">
                  <span>Total:</span>
                  <div className="total-prices">
                    {hasDiscount && <span className="price-original" style={{ textDecoration: "line-through", color: "#94A3B8" }}>{formatUSD(price, exchangeRate)}</span>}
                    <strong style={{ fontSize: "1.15rem", color: "#16A34A" }}>{formatUSD(finalPrice, exchangeRate)}</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Recipient info — varies per service kind */}
            {selectedService?.requires_recipient_info && !["free_text", "email", "wallet", "account_id"].includes(selectedService?.recipient_kind) && (
              <div className="transaction-section" data-testid="recipient-info-section">
                <label className="form-label" style={{ fontWeight: 700, color: "#1E40AF" }}>
                  Informations du destinataire
                </label>
                {selectedService.recipient_kind === "wise" ? (
                  <>
                    <input className="form-input" placeholder="Email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-email-wise" />
                    <input className="form-input" placeholder="Nom du bénéficiaire" value={recBenefName} onChange={(e) => setRecBenefName(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-name-wise" />
                    <input className="form-input" placeholder="Wise Tag (commençant par @)" value={recWiseTag} onChange={(e) => setRecWiseTag(e.target.value)} data-testid="rec-wise-tag" />
                  </>
                ) : selectedService.recipient_kind === "freefire" ? (
                  <>
                    <input className="form-input" placeholder="Nom du joueur" value={recPlayerName} onChange={(e) => setRecPlayerName(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-player-name" />
                    <input className="form-input" placeholder="ID du compte" value={recGameId} onChange={(e) => setRecGameId(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-game-id" />
                    <input className="form-input" type="text" placeholder="Mot de passe (optionnel pour certains jeux)" value={recGamePassword} onChange={(e) => setRecGamePassword(e.target.value)} data-testid="rec-game-password" />
                  </>
                ) : selectedService.recipient_kind === "efootball" ? (
                  <>
                    <input className="form-input" placeholder="Nom du joueur" value={recPlayerName} onChange={(e) => setRecPlayerName(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-player-name" />
                    <input className="form-input" placeholder="ID eFootball" value={recGameId} onChange={(e) => setRecGameId(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-game-id" />
                    <input className="form-input" type="text" placeholder="Mot de passe du compte (modpass)" value={recGamePassword} onChange={(e) => setRecGamePassword(e.target.value)} data-testid="rec-game-password" />
                    <div style={{ fontSize: "0.7rem", color: "#92400E", background: "#FFFBEB", padding: "0.4rem 0.5rem", borderRadius: 6, marginTop: 6 }}>
                      🔒 Vos identifiants sont transmis uniquement à l'admin pour la recharge. Changez votre mot de passe après la transaction par sécurité.
                    </div>
                  </>
                ) : selectedService.recipient_kind === "boost" ? (
                  <>
                    <div style={{ marginBottom: 6 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Plateforme</label>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[
                          { key: "facebook", label: "Facebook", color: "#1877F2" },
                          { key: "tiktok", label: "TikTok", color: "#000000" },
                          { key: "instagram", label: "Instagram", color: "#E1306C" },
                        ].map(p => (
                          <button key={p.key} type="button"
                            onClick={() => setRecBoostPlatform(p.key)}
                            data-testid={`boost-platform-${p.key}`}
                            className="btn btn-sm"
                            style={{ flex: 1, background: recBoostPlatform === p.key ? p.color : "#F1F5F9", color: recBoostPlatform === p.key ? "white" : "#0F172A", fontWeight: 700, fontSize: "0.78rem" }}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input className="form-input" placeholder="Lien du post à booster (https://...)" value={recBoostPostLink} onChange={(e) => setRecBoostPostLink(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-boost-link" />
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{ fontSize: "0.75rem" }}>Budget / jour ($)</label>
                        <input className="form-input" type="number" min="1" max="100" step="1" value={recBoostBudget} onChange={(e) => setRecBoostBudget(e.target.value)} data-testid="rec-boost-budget" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label" style={{ fontSize: "0.75rem" }}>Nombre de jours</label>
                        <input className="form-input" type="number" min="1" max="90" step="1" value={recBoostDays} onChange={(e) => setRecBoostDays(e.target.value)} data-testid="rec-boost-days" />
                      </div>
                    </div>
                    {(() => {
                      const budget = parseFloat(recBoostBudget) || 0;
                      const days = parseInt(recBoostDays) || 0;
                      const basePrice = parseFloat(selectedService?.boost_base_price_htg || 200);
                      const totalG = +(basePrice * budget * days).toFixed(2);
                      const totalUsd = exchangeRate > 0 ? +(totalG / exchangeRate).toFixed(2) : 0;
                      return (
                        <div data-testid="boost-price-preview" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", padding: "0.55rem 0.7rem", borderRadius: 8, fontSize: "0.85rem", fontWeight: 700, color: "#1E40AF" }}>
                          💰 Total : {totalG.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} G ({totalUsd} $)
                          <div style={{ fontSize: "0.65rem", color: "#475569", fontWeight: 500, marginTop: 2 }}>
                            {basePrice} G × {budget} $/jour × {days} jour(s)
                          </div>
                        </div>
                      );
                    })()}
                  </>
                ) : selectedService.recipient_kind === "giftcard_email" ? (
                  <input className="form-input" type="email" placeholder="Votre adresse email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)} data-testid="rec-giftcard-email" />
                ) : (
                  <>
                    <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.5rem" }}>
                      <button type="button" className="btn btn-sm" data-testid="rec-tab-email"
                        style={{ background: recipientType === "email" ? "#2563EB" : "#E2E8F0", color: recipientType === "email" ? "white" : "#0F172A", flex: 1 }}
                        onClick={() => setRecipientType("email")}>Email</button>
                      <button type="button" className="btn btn-sm" data-testid="rec-tab-crypto"
                        style={{ background: recipientType === "crypto" ? "#2563EB" : "#E2E8F0", color: recipientType === "crypto" ? "white" : "#0F172A", flex: 1 }}
                        onClick={() => setRecipientType("crypto")}>Adresse crypto</button>
                    </div>
                    {recipientType === "email" ? (
                      <>
                        <input className="form-input" placeholder="Email du compte" value={recEmail} onChange={(e) => setRecEmail(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-email" />
                        <input className="form-input" placeholder="Nom du bénéficiaire" value={recBenefName} onChange={(e) => setRecBenefName(e.target.value)} data-testid="rec-name" />
                      </>
                    ) : (
                      <>
                        <input className="form-input" placeholder="Type de crypto (ex: BTC, USDT)" value={recCrypto} onChange={(e) => setRecCrypto(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-crypto" />
                        <input className="form-input" placeholder="Réseau (ex: TRC20, ERC20)" value={recNetwork} onChange={(e) => setRecNetwork(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-network" />
                        <input className="form-input" placeholder="Adresse crypto" value={recAddress} onChange={(e) => setRecAddress(e.target.value)} style={{ marginBottom: 6 }} data-testid="rec-address" />
                        <input className="form-input" placeholder="Lien associé (optionnel)" value={recLink} onChange={(e) => setRecLink(e.target.value)} data-testid="rec-link" />
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Payment method (hidden for balance-only services) */}
            {!selectedService?.balance_payment_only && (
              <>
                <div className="payment-method-section">
                  <label className="form-label">Mode de paiement</label>
                  <div className="payment-methods">
                    <button
                      className={`method-btn ${paymentMethod === "qr" ? "active" : ""}`}
                      onClick={() => setPaymentMethod("qr")}
                      data-testid="method-qr"
                    >
                      QR Code
                    </button>
                    <button
                      className={`method-btn ${paymentMethod === "phone" ? "active" : ""}`}
                      onClick={() => setPaymentMethod("phone")}
                      data-testid="method-phone"
                    >
                      <Phone size={14} /> Téléphone
                    </button>
                  </div>
                </div>

                {paymentMethod === "qr" ? (
                  <div className="qr-section" data-testid="qr-section">
                    <p className="qr-instruction">Scannez un QR Code pour payer via MonCash</p>
                    <div className="qr-carousel">
                      {QR_CODES.map((qr, idx) => (
                        <div key={qr.number || qr.label || idx} className="qr-card">
                          <img src={qr.image} alt={qr.label} className="qr-image" />
                          <div className="qr-info">
                            <div className="qr-name">{qr.name}</div>
                            <div className="qr-number">{qr.number}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="phone-payment-section" data-testid="phone-payment-section">
                    <p className="payment-instruction">Envoyez le paiement au numéro:</p>
                    <div className="payment-phone-display">{PAYMENT_PHONE}</div>
                  </div>
                )}
              </>
            )}

            {/* "Informations supplémentaires" + "Durée souhaitée" — only for streaming services
                (Netflix, Prime Video). Other services don't need these fields. */}
            {selectedService?.requires_extras && (
              <>
                <div className="transaction-section">
                  <label className="form-label">Informations supplémentaires</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={`Ex: Nom de compte, identifiant, numéro...`}
                    value={profileName}
                    onChange={e => setProfileName(e.target.value)}
                    data-testid="profile-name-input"
                  />
                </div>

                <div className="transaction-section">
                  <label className="form-label">Durée souhaitée (jours)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Ex: 30"
                    value={orderDurationDays}
                    onChange={e => setOrderDurationDays(e.target.value)}
                    min="1"
                    data-testid="duration-days-input"
                  />
                </div>
              </>
            )}

            {/* Proof upload + balance toggle — hidden for balance-only services */}
            {!selectedService?.balance_payment_only && (
              <>
                <div className="transaction-section">
                  <label className="form-label">Preuve de paiement (capture d'écran)</label>
                  <div style={{ border: "2px dashed #CBD5E1", borderRadius: "10px", padding: "1rem", textAlign: "center", cursor: "pointer", background: paymentProofUrl ? "#F0FDF4" : "#F8FAFC" }}
                    onClick={() => document.getElementById("proof-upload").click()}>
                    <input id="proof-upload" type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleProofUpload} data-testid="proof-upload-input" />
                    {paymentProofUrl ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                        <CheckCircle size={20} style={{ color: "#16A34A" }} />
                        <span style={{ color: "#16A34A", fontWeight: 600, fontSize: "0.85rem" }}>Preuve téléversée</span>
                      </div>
                    ) : (
                      <div>
                        <Upload size={24} style={{ color: "#94A3B8", margin: "0 auto" }} />
                        <div style={{ fontSize: "0.8rem", color: "#64748B", marginTop: "0.3rem" }}>Cliquez pour téléverser</div>
                      </div>
                    )}
                  </div>
                  {paymentProofUrl && paymentProofUrl.startsWith("data:image") && (
                    <img src={paymentProofUrl} alt="Preuve" style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8, marginTop: "0.5rem", objectFit: "contain" }} />
                  )}
                </div>

                {/* Balance option toggle */}
                <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                  <button type="button" onClick={() => setPaymentMethod("qr")} className="btn btn-sm" data-testid="pay-method-qr"
                    style={{ background: paymentMethod === "qr" ? "#2563EB" : "#E2E8F0", color: paymentMethod === "qr" ? "white" : "#0F172A", flex: 1 }}>
                    QR / Capture
                  </button>
                  <button type="button" onClick={async () => {
                      setPaymentMethod("balance");
                      // Refresh balance the moment user selects "Balance interne".
                      try {
                        const r = await apiCall("get", "/balance");
                        setClientBalanceHtg(Number(r.data?.balance) || 0);
                      } catch { /* silent */ }
                    }} className="btn btn-sm" data-testid="pay-method-balance"
                    style={{ background: paymentMethod === "balance" ? "#16A34A" : "#E2E8F0", color: paymentMethod === "balance" ? "white" : "#0F172A", flex: 1 }}>
                    Balance interne
                  </button>
                </div>
                {paymentMethod === "balance" && (
                  <div style={{ padding: "0.5rem", background: "#F0FDF4", borderRadius: "8px", fontSize: "0.75rem", color: "#14532D", marginBottom: "0.5rem" }}>
                    Le montant sera débité de votre solde. Votre commande sera traitée manuellement par un affilié ou un administrateur.
                  </div>
                )}
              </>
            )}

            {/* Balance-only services: dedicated balance card with USD info + insufficient balance redirect */}
            {selectedService?.balance_payment_only && (
              <div style={{ background: "#F0FDF4", padding: "0.75rem 0.85rem", borderRadius: 10, border: "1px solid #BBF7D0", marginTop: "0.4rem", marginBottom: "0.4rem" }} data-testid="balance-only-section">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: "0.8rem", color: "#15803D", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    Solde actuel
                    <button type="button" data-testid="refresh-balance-btn" title="Rafraîchir le solde"
                      onClick={async () => {
                        try {
                          const r = await apiCall("get", "/balance");
                          const next = Number(r.data?.balance) || 0;
                          const rate = exchangeRate || 140;
                          if (next > clientBalanceHtg + 0.01) {
                            const diffUsd = (next - clientBalanceHtg) / rate;
                            toast.success(`💰 +${diffUsd.toFixed(2)} $ — Nouveau solde : ${(next / rate).toFixed(2)} $`, { duration: 7000 });
                          } else {
                            toast.success("Solde rafraîchi");
                          }
                          setClientBalanceHtg(next);
                        } catch { toast.error("Erreur"); }
                      }}
                      style={{ background: "#DCFCE7", color: "#15803D", border: "1px solid #BBF7D0", borderRadius: 4, padding: "1px 6px", fontSize: "0.65rem", cursor: "pointer", fontWeight: 600 }}>
                      ↻ Actualiser
                    </button>
                  </span>
                  <span style={{ fontSize: "0.95rem", fontWeight: 800, color: "#15803D" }}>
                    {formatUSD(clientBalanceHtg, exchangeRate)}
                  </span>
                </div>
                {finalPrice > clientBalanceHtg ? (
                  <div style={{ background: "#FEF2F2", color: "#B91C1C", padding: "0.5rem 0.6rem", borderRadius: 8, fontSize: "0.78rem", border: "1px solid #FECACA" }}>
                    Solde insuffisant — il manque <strong>{formatUSD(finalPrice - clientBalanceHtg, exchangeRate)}</strong>.
                    <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 6, width: "100%" }} data-testid="goto-balance-recharge"
                      onClick={() => { setShowPayment(false); setActiveView("balance"); }}>
                      Rechargez votre solde
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: "0.75rem", color: "#15803D" }}>
                    Le montant sera débité de votre solde après confirmation.
                  </div>
                )}
              </div>
            )}

            <button
              className="btn btn-primary btn-full"
              onClick={handleSubmitOrder}
              disabled={
                isSubmitting ||
                (paymentMethod !== "balance" && !paymentProofUrl) ||
                (selectedPlan.price === 0 && !selectedPlan.is_exchange && !customAmountUsd) ||
                (selectedService?.balance_payment_only && finalPrice > clientBalanceHtg)
              }
              data-testid="submit-order-btn"
            >
              <Send size={18} />
              {isSubmitting ? "Envoi..." : paymentMethod === "balance" ? "Payer avec mon solde" : "Envoyer la commande"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-dashboard has-sidebar" data-testid="client-dashboard">
      <PromoVideoPopup role="client" />
      <Sidebar
        title="SMARTECH"
        subtitle={`Client · ${clientUser?.name || clientUser?.phone || ""}`}
        accent="#2563EB"
        active={activeView}
        onSelect={setActiveView}
        onLogout={onLogout}
        items={[
          { key: "services", label: "Services", icon: ShoppingCart, testid: "tab-services" },
          { key: "orders", label: "Commandes", icon: Clock, testid: "tab-orders" },
          { key: "balance", label: "Mon solde", icon: Wallet, testid: "tab-client-balance" },
          { key: "account", label: "Compte", icon: UserCheck, testid: "tab-account" },
          { key: "history", label: "Historique", icon: History, testid: "tab-client-history" },
          { key: "formations", label: "Formations", icon: BookOpen, testid: "tab-formations" },
          { key: "subscriptions", label: "Abonnements", icon: CalendarDays, testid: "tab-subscriptions" },
          { key: "game-topup", label: "Topup Jeux", icon: Gamepad2, testid: "tab-game-topup" },
          { key: "smm", label: "SMM Catalogue", icon: Megaphone, testid: "tab-client-smm" },
          { key: "crypto", label: "Échange Crypto", icon: Bitcoin, testid: "tab-crypto" },
          { key: "p2p", label: "Marketplace P2P", icon: ArrowRightLeft, testid: "tab-client-p2p" },
        ]}
      />
      <div className="sidebar-content">
      <header className="client-header">
        <h1 className="header-title-sm">SMARTECH</h1>
        <div className="header-user">
          <NotificationCenter apiCall={apiCall} role="client" />
          <span className="client-name">{clientUser?.name || clientUser?.phone}</span>
          <button onClick={onLogout} className="btn-logout" data-testid="client-logout-btn"><LogOut size={18} /></button>
        </div>
      </header>

      {/* Stats bar */}
      {stats && (
        <div className="client-stats-bar">
          <div className="cstat"><ShoppingCart size={14} /><span>{stats.total_orders} commandes</span></div>
          <div className="cstat"><Clock size={14} /><span>{stats.pending_orders} en attente</span></div>
          <div className="cstat" data-testid="client-stats-balance"><DollarSign size={14} /><span>{formatUSD(stats.total_spent, exchangeRate)}</span></div>
        </div>
      )}

      {/* Discount Banner */}
      {activeDiscount && activeDiscount.discount > 0 && (
        <div className="discount-banner" data-testid="discount-banner">
          <Percent size={16} />
          <div>
            <span>Bonus actif: <strong>{activeDiscount.discount}%</strong> de reduction</span>
            <div style={{ fontSize: "0.65rem", opacity: 0.8, marginTop: "0.1rem" }}>Ce bonus est utilisable une seule fois par client.</div>
          </div>
        </div>
      )}

      {/* Nav tabs replaced by Sidebar */}

      <div className="client-content">
        {/* Welcome WhatsApp banner — shown when client has no phone (Google
            sign-up case, or anyone who hasn't added their number). */}
        {!waBannerDismissed && clientUser && !(clientUser.phone || "").trim() && (
          <div style={{
            padding: "0.8rem 0.9rem",
            marginBottom: "0.75rem",
            borderRadius: 12,
            background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
            border: "1.5px solid #6EE7B7",
            position: "relative",
          }} data-testid="client-whatsapp-welcome-banner">
            <button
              onClick={() => setWaBannerDismissed(true)}
              aria-label="Fermer"
              style={{ position: "absolute", top: 6, right: 8, background: "transparent", border: "none", color: "#065F46", cursor: "pointer", fontSize: "1rem", fontWeight: 700 }}
            >×</button>
            <div style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
              <div style={{ background: "#10B981", color: "#FFF", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: "1rem" }}>👋</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "#065F46", marginBottom: "0.2rem" }}>
                  Bienvenue !
                </div>
                <div style={{ fontSize: "0.78rem", color: "#065F46", lineHeight: 1.4, marginBottom: "0.5rem" }}>
                  Pour améliorer votre expérience et faciliter la communication, veuillez ajouter
                  votre numéro WhatsApp dans votre profil. Cela nous permettra de vous contacter
                  rapidement concernant vos commandes et notifications importantes.
                </div>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  <input
                    className="form-input"
                    placeholder="+509 XXXX XXXX"
                    value={waInput}
                    onChange={(e) => setWaInput(e.target.value)}
                    style={{ flex: "1 1 180px", fontSize: "0.85rem", padding: "0.5rem 0.7rem" }}
                    data-testid="client-whatsapp-input"
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={waSaving}
                    data-testid="client-whatsapp-save"
                    onClick={async () => {
                      const digits = waInput.replace(/[^0-9]/g, "");
                      if (digits.length < 8) { toast.error("Min. 8 chiffres"); return; }
                      setWaSaving(true);
                      try {
                        const res = await apiCall("post", "/client/update-whatsapp", { whatsapp: waInput });
                        toast.success("Numéro WhatsApp enregistré !");
                        // Patch local user so the banner disappears
                        const updated = { ...clientUser, phone: res.data.whatsapp };
                        localStorage.setItem("client_user", JSON.stringify(updated));
                        setWaInput("");
                        setWaBannerDismissed(true);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur");
                      } finally {
                        setWaSaving(false);
                      }
                    }}
                  >
                    {waSaving ? "..." : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email notification prompt */}
        {showEmailPrompt && (
          <div style={{ padding: "0.6rem", marginBottom: "0.75rem", borderRadius: "10px", background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#1E40AF", marginBottom: "0.3rem" }}>Recevez vos notifications par email!</div>
            <div style={{ display: "flex", gap: "0.3rem" }}>
              <input className="form-input" style={{ fontSize: "0.78rem", flex: 1 }} placeholder="Votre email" value={notifEmail} onChange={e => setNotifEmail(e.target.value)} data-testid="client-notif-email" />
              <button className="btn btn-primary btn-sm" onClick={async () => {
                if (!notifEmail.trim()) return;
                try { await apiCall("post", "/client/update-notification-email", { notification_email: notifEmail.trim() }); toast.success("Email enregistré!"); setShowEmailPrompt(false); } catch { toast.error("Erreur"); }
              }}>OK</button>
            </div>
          </div>
        )}
        {/* Services View */}
        {activeView === "services" && (
          <>
            {/* Balance widget + affiliate selector */}
            <BalanceWidget apiCall={apiCall} API={API} tokenKey="client_token" role="client" />

            {/* Cashback widget removed per business decision */}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0.7rem", background: "#F8FAFC", borderRadius: "10px", border: "1px solid #E2E8F0", marginBottom: "0.8rem", flexWrap: "wrap", gap: "0.4rem" }}>
              <div style={{ fontSize: "0.75rem", color: "#475569" }}>
                Affilié qui vous sert: <b>{clientUser?.affiliate_code || "Aucun"}</b>
              </div>
              <AffiliateSelector apiCall={apiCall} currentCode={clientUser?.affiliate_code} compact onSelected={() => fetchData()} />
            </div>

            {/* Explainer video */}
            {clientVideo && (
              <div style={{ marginBottom: "0.75rem", borderRadius: "10px", overflow: "hidden", background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                {clientVideo.title && <div style={{ padding: "0.4rem 0.6rem", fontWeight: 700, fontSize: "0.82rem", color: "#1E293B" }}><Play size={14} style={{ verticalAlign: "middle" }} /> {clientVideo.title}</div>}
                <iframe src={clientVideo.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").replace("vimeo.com/", "player.vimeo.com/video/")}
                  style={{ width: "100%", height: "200px", border: "none" }} allowFullScreen title="Comment acheter" />
              </div>
            )}
            <div className="search-bar">
              <Search size={16} />
              <input
                type="text" placeholder="Rechercher un service..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="search-input" data-testid="search-services"
              />
            </div>

            <div className="services-list" data-testid="services-list">
              {filteredServices.map(service => {
                const isOutOfStock = !!service.out_of_stock;
                return (
                <div
                  key={service.id}
                  className="service-card"
                  data-testid={`service-${service.id}`}
                  style={isOutOfStock ? { opacity: 0.7, position: "relative" } : { position: "relative" }}
                >
                  {isOutOfStock && (
                    <div
                      data-testid={`out-of-stock-badge-${service.id}`}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: "#DC2626",
                        color: "white",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 999,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        boxShadow: "0 2px 6px rgba(220,38,38,0.35)",
                        zIndex: 2,
                      }}
                    >
                      Indisponible
                    </div>
                  )}
                  <div className="service-header">
                    <div className="service-icon-wrap">
                      <ServiceIcon icon={service.icon} />
                    </div>
                    <div>
                      <h3 className="service-name">{service.name}</h3>
                      {service.subtitle && <p className="service-subtitle">{service.subtitle}</p>}
                      {/* Rate label removed — global rate is shown site-wide (single source of truth). */}
                      {service.minimum && <p className="service-min">Minimum: {service.minimum}</p>}
                      {service.client_fee_percent > 0 && (
                        <p style={{ fontSize: "0.7rem", color: "#92400E", margin: "2px 0 0 0", fontWeight: 600 }}>
                          Frais service +{service.client_fee_percent}%
                        </p>
                      )}
                      {isOutOfStock && (
                        <p style={{ fontSize: "0.72rem", color: "#DC2626", margin: "4px 0 0 0", fontWeight: 600 }}>
                          Service temporairement en rupture. Réessayez plus tard.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="plans-grid">
                    {service.plans.map(plan => (
                      <button
                        key={plan.id}
                        className="plan-card"
                        onClick={() => {
                          if (isOutOfStock) {
                            toast.error("Ce service est actuellement en rupture de stock. Veuillez réessayer plus tard.");
                            return;
                          }
                          handleSelectPlan(service, plan);
                        }}
                        disabled={isOutOfStock}
                        data-testid={`plan-${plan.id}`}
                        style={isOutOfStock ? { cursor: "not-allowed", opacity: 0.55 } : undefined}
                      >
                        <span className="plan-label">{plan.name}</span>
                        <span className="plan-price">
                          {plan.price > 0
                            ? (
                              service.service_id === "echange_moncash_natcash"
                                ? formatUSD(plan.price, exchangeRate)
                                : formatUSD(plan.price, exchangeRate)
                            )
                            : "Personnalisé"}
                        </span>
                        <ChevronRight size={14} className="plan-arrow" />
                      </button>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}

        {/* Orders View */}
        {activeView === "orders" && (
          <div className="orders-list" data-testid="orders-list">
            {orders.length === 0 ? (
              <div className="empty-state">
                <ShoppingCart size={32} className="empty-icon" />
                <p>Aucune commande</p>
              </div>
            ) : (
              orders.map(order => (
                <div key={order.id} className="order-card" data-testid={`order-${order.id}`}>
                  <div className="order-info">
                    <div className="order-service">
                      {order.service_name} - {order.plan_name}
                      {order.paid_from_balance && (
                        <span style={{ marginLeft: "0.4rem", fontSize: "0.62rem", background: "#F0FDF4", color: "#166534", padding: "0.1rem 0.35rem", borderRadius: "4px", fontWeight: 700, verticalAlign: "middle" }}>
                          Payé par solde
                        </span>
                      )}
                      {order.requires_manual_delivery && order.status === "pending" && (
                        <span style={{ marginLeft: "0.3rem", fontSize: "0.62rem", background: "#FEF3C7", color: "#92400E", padding: "0.1rem 0.35rem", borderRadius: "4px", fontWeight: 700, verticalAlign: "middle" }}>
                          Traitement manuel requis
                        </span>
                      )}
                    </div>
                    <div className="order-details">
                      <span>{formatUSD(order.price, exchangeRate)}</span>
                      {order.transaction_id && <span>ID: {order.transaction_id}</span>}
                      {order.cashback_credited > 0 && false && (
                        <span style={{ color: "#166534", fontWeight: 700 }}>+{order.cashback_credited} cashback</span>
                      )}
                    </div>
                    <div className="order-date">{formatDate(order.created_at)}</div>
                    {/* Recipient info — client confirms what they entered. */}
                    <RecipientInfo payload={order.client_payload} testidPrefix={`client-recipient-${order.id}`} />
                    {order.status === "validated" && !order.affiliate_validated && (
                      <div style={{ marginTop: "0.4rem" }}>
                        <AffiliateSelector apiCall={apiCall} currentCode={order.affiliate_code} compact
                          onSelected={async (newCode) => {
                            try {
                              await apiCall("post", `/client/change-affiliate/${order.id}`, { affiliate_code: newCode });
                              toast.success(`Commande réassignée à ${newCode}`);
                              fetchData();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Erreur");
                            }
                          }} />
                      </div>
                    )}
                  </div>
                  {getStatusBadge(order.status)}
                </div>
              ))
            )}
          </div>
        )}

        {/* Account View */}
        {activeView === "account" && (
          <div className="account-section" data-testid="account-section">
            {/* Secondary WhatsApp request widget */}
            <ClientWhatsAppSecondary apiCall={apiCall} />

            {/* Affiliate Code */}
            <div className="account-card">
              <h3 className="account-card-title"><UserCheck size={18} /> Code Affilié</h3>
              {activeDiscount?.has_affiliate ? (
                <div className="affiliate-locked" data-testid="affiliate-locked">
                  <Lock size={16} />
                  <div>
                    <div className="locked-code">{activeDiscount.affiliate_code}</div>
                    <div className="locked-info">Code verrouillé - 10% de réduction active</div>
                  </div>
                </div>
              ) : (
                <div className="affiliate-set-form">
                  <p className="affiliate-info-text">Entrez un code affilié pour bénéficier de 10% de réduction sur tous vos achats.</p>
                  <p className="affiliate-warn-text"><Lock size={12} /> Une fois validé, le code est définitif.</p>
                  <div className="affiliate-input-row">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Code affilié"
                      value={affiliateCodeInput}
                      onChange={e => setAffiliateCodeInput(e.target.value.toUpperCase())}
                      data-testid="set-affiliate-code-input"
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSetAffiliateCode}
                      disabled={settingCode || !affiliateCodeInput.trim()}
                      data-testid="set-affiliate-code-btn"
                    >
                      {settingCode ? "..." : "Activer"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Active Discount Info */}
            <div className="account-card">
              <h3 className="account-card-title"><Tag size={18} /> Mes Bonus</h3>
              {activeDiscount?.discount > 0 ? (
                <div className="discount-info-card">
                  <div className="discount-big">{activeDiscount.discount}%</div>
                  <div className="discount-label">Bonus actif</div>
                  <div style={{ fontSize: "0.7rem", color: "#475569", marginTop: "0.25rem" }}>Ce bonus est utilisable une seule fois par client.</div>
                </div>
              ) : (
                <p className="no-discount-text">Aucun bonus actif pour le moment.</p>
              )}
            </div>

            {/* Client Info */}
            {stats && (
              <div className="account-card">
                <h3 className="account-card-title"><Phone size={18} /> Mon Compte</h3>
                <div className="account-info-row"><span>Téléphone:</span><strong>{stats.phone}</strong></div>
                <div className="account-info-row"><span>Nom:</span><strong>{stats.name}</strong></div>
                <div className="account-info-row"><span>Commandes:</span><strong>{stats.total_orders}</strong></div>
                <div className="account-info-row"><span>Total dépensé:</span><strong>{formatUSD(stats.total_spent, exchangeRate)}</strong></div>
              </div>
            )}

            {/* Self-service password change */}
            <div className="account-card" data-testid="client-change-password-card">
              <h3 className="account-card-title"><Lock size={18} /> Changer mon mot de passe</h3>
              <input type="password" className="form-input" placeholder="Mot de passe actuel"
                value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} style={{ marginBottom: 6 }}
                data-testid="pw-current-input" />
              <input type="password" className="form-input" placeholder="Nouveau mot de passe (min. 6)"
                value={pwNew} onChange={e => setPwNew(e.target.value)} style={{ marginBottom: 6 }}
                data-testid="pw-new-input" />
              <input type="password" className="form-input" placeholder="Confirmer le nouveau mot de passe"
                value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} style={{ marginBottom: 6 }}
                data-testid="pw-confirm-input" />
              <button className="btn btn-primary btn-sm" disabled={pwSaving} data-testid="pw-save-btn"
                onClick={async () => {
                  if (pwNew.length < 6) { toast.error("Min. 6 caractères"); return; }
                  if (pwNew !== pwConfirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
                  setPwSaving(true);
                  try {
                    await apiCall("post", "/client/change-password", {
                      current_password: pwCurrent,
                      new_password: pwNew,
                    });
                    toast.success("Mot de passe mis à jour");
                    setPwCurrent(""); setPwNew(""); setPwConfirm("");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erreur");
                  } finally { setPwSaving(false); }
                }}>
                {pwSaving ? "Enregistrement..." : "Enregistrer le nouveau mot de passe"}
              </button>
            </div>
          </div>
        )}

        {/* History View */}
        {activeView === "history" && (
          <div className="client-history-list" data-testid="client-history-list">
            {clientHistory.length === 0 ? (
              <div className="empty-state">
                <History size={32} className="empty-icon" />
                <p>Aucune activité</p>
              </div>
            ) : (
              clientHistory.map((item, idx) => (
                <div key={item._id || item.id || `${item.timestamp}-${idx}`} className="history-entry">
                  <div className={`hist-dot ${item.action.includes("validee") ? "success" : item.action.includes("rejetee") ? "error" : "pending"}`} />
                  <div className="hist-content">
                    <div className="hist-action">{item.action.replace(/_/g, " ")}</div>
                    <div className="hist-details">{item.details}</div>
                    <div className="hist-time">{formatDate(item.timestamp)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Formations View */}
        {activeView === "formations" && (
          <div data-testid="formations-view">
            {/* Viewing a purchased course */}
            {viewingCourse ? (
              <div data-testid="course-viewer">
                <button className="back-btn" style={{ marginBottom: "0.5rem" }} onClick={() => setViewingCourse(null)}>
                  <ChevronLeft size={16} /> Retour aux formations
                </button>
                <div className="card" style={{ padding: "1rem" }}>
                  <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1E293B", marginBottom: "0.5rem" }}>{viewingCourse.title}</h3>
                  {viewingCourse.description && <p style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "0.75rem" }}>{viewingCourse.description}</p>}
                  
                  {/* Videos */}
                  {viewingCourse.videos && viewingCourse.videos.length > 0 && (
                    <div style={{ marginBottom: "0.75rem" }}>
                      <h4 style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem", color: "#1E293B" }}><Video size={14} style={{ verticalAlign: "middle" }} /> Videos ({viewingCourse.videos.length})</h4>
                      {viewingCourse.videos.map((v, i) => (
                        <div key={v.id || i} style={{ marginBottom: "0.75rem" }}>
                          <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.3rem" }}>{v.title}</div>
                          {v.type === "external" ? (
                            <div className="video-secure-wrap" style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: 8, background: "#000" }} onContextMenu={e => e.preventDefault()}>
                              <iframe
                                src={(() => {
                                  let raw = v.url || "";
                                  // Vimeo: convert vimeo.com/ID to player.vimeo.com/video/ID
                                  const vimeoMatch = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                                  if (vimeoMatch) {
                                    return `https://player.vimeo.com/video/${vimeoMatch[1]}?title=0&byline=0&portrait=0&badge=0&share=0&like=0&watch_later=0`;
                                  }
                                  // YouTube: convert to embed URL with security params
                                  let url = raw.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/");
                                  const sep = url.includes("?") ? "&" : "?";
                                  return `${url}${sep}modestbranding=1&rel=0&showinfo=0&fs=0&iv_load_policy=3&cc_load_policy=0&controls=1&disablekb=0`;
                                })()}
                                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                                allow="accelerometer; autoplay; encrypted-media; gyroscope"
                                title={v.title}
                              />
                              {/* Top overlay - blocks YouTube title and logo (prevents clicking to YouTube) */}
                              <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "46px", background: "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%)", zIndex: 20, cursor: "default" }} />
                              {/* Bottom-right overlay - covers share button and YouTube logo */}
                              <div style={{ position: "absolute", bottom: 0, right: 0, width: "220px", height: "44px", background: "transparent", zIndex: 20, cursor: "default" }} />
                              {/* Watermark center */}
                              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%) rotate(-20deg)", color: "rgba(255,255,255,0.08)", fontSize: "1.2rem", fontWeight: 900, pointerEvents: "none", whiteSpace: "nowrap", textShadow: "1px 1px 3px rgba(0,0,0,0.2)", zIndex: 25, letterSpacing: "0.05em" }}>
                                {clientUser?.name || clientUser?.phone}
                              </div>
                              {/* Watermark bottom-right */}
                              <div style={{ position: "absolute", bottom: 50, right: 10, color: "rgba(255,255,255,0.35)", fontSize: "0.65rem", fontWeight: 700, pointerEvents: "none", textShadow: "1px 1px 2px rgba(0,0,0,0.5)", zIndex: 25 }}>
                                {clientUser?.phone} - SMARTECH
                              </div>
                            </div>
                          ) : (
                            <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", background: "#000" }} onContextMenu={e => e.preventDefault()}>
                              <video
                                controls
                                controlsList="nodownload"
                                disablePictureInPicture
                                style={{ width: "100%", display: "block" }}
                                src={`${API}/stream/video/${v.filename}?token=${localStorage.getItem("client_token")}`}
                              />
                              {/* Watermark overlay */}
                              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%) rotate(-25deg)", color: "rgba(255,255,255,0.15)", fontSize: "1.5rem", fontWeight: 900, pointerEvents: "none", whiteSpace: "nowrap", textShadow: "2px 2px 4px rgba(0,0,0,0.3)", zIndex: 10, letterSpacing: "0.1em" }}>
                                {clientUser?.name || clientUser?.phone}
                              </div>
                              <div style={{ position: "absolute", bottom: 8, right: 8, color: "rgba(255,255,255,0.4)", fontSize: "0.65rem", fontWeight: 700, pointerEvents: "none", zIndex: 10 }}>
                                {clientUser?.phone} - SMARTECH
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Documents */}
                  {viewingCourse.documents && viewingCourse.documents.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem", color: "#1E293B" }}><FileText size={14} style={{ verticalAlign: "middle" }} /> Documents ({viewingCourse.documents.length})</h4>
                      {viewingCourse.documents.map((d, i) => (
                        <a key={d.id || i} href={`${API}/view/document/${d.filename}?token=${localStorage.getItem("client_token")}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem", background: "#F8FAFC", borderRadius: 6, marginBottom: "0.3rem", fontSize: "0.8rem", textDecoration: "none", color: "#2563EB" }}>
                          <FileText size={14} /> {d.title}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Actions: Certificate, Chat, Review */}
                  <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: "0.75rem", marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {/* Certificate */}
                    {(() => {
                      const cert = getCertForCourse(viewingCourse.id);
                      if (cert) {
                        return (
                          <a href={`${API}/view/document/${cert.certificate_filename}?token=${localStorage.getItem("client_token")}`} target="_blank" rel="noopener noreferrer"
                            className="btn btn-success btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", width: "fit-content" }} data-testid="download-certificate">
                            <Download size={14} /> Telecharger mon certificat (PDF)
                          </a>
                        );
                      }
                      const pending = myCertificates.find(c => c.course_id === viewingCourse.id && c.status === "pending");
                      if (pending) {
                        return <span style={{ fontSize: "0.8rem", color: "#D97706" }}><Clock size={14} style={{ verticalAlign: "middle" }} /> Demande de certificat en cours...</span>;
                      }
                      if (viewingCourse.professor_id) {
                        return (
                          <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", border: "1px solid #93C5FD", width: "fit-content" }} onClick={() => handleRequestCertificate(viewingCourse.id)} data-testid="request-certificate">
                            <Award size={14} /> Demander un certificat
                          </button>
                        );
                      }
                      return null;
                    })()}

                    {/* Chat with professor */}
                    {viewingCourse.professor_id && (
                      <div>
                        <button className="btn btn-sm" style={{ background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0", width: "fit-content" }} onClick={() => openChat(viewingCourse.professor_id, viewingCourse.id)} data-testid="open-chat-prof">
                          <MessageCircle size={14} /> Contacter le professeur
                        </button>
                        {chatProfId === viewingCourse.professor_id && (
                          <div className="card" style={{ marginTop: "0.5rem", padding: "0.5rem" }}>
                            <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: "0.5rem" }}>
                              {chatMessages.length === 0 ? (
                                <div style={{ textAlign: "center", fontSize: "0.75rem", color: "#94A3B8", padding: "1rem" }}>Aucun message</div>
                              ) : chatMessages.map(m => (
                                <div key={m.id} style={{ display: "flex", justifyContent: m.sender === "client" ? "flex-end" : "flex-start", marginBottom: "0.3rem" }}>
                                  <div style={{ maxWidth: "75%", padding: "0.35rem 0.5rem", borderRadius: 10, background: m.sender === "client" ? "#2563EB" : "#F1F5F9", color: m.sender === "client" ? "white" : "#1E293B", fontSize: "0.8rem" }}>
                                    {m.content}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: "0.3rem" }}>
                              <input className="form-input" style={{ flex: 1, fontSize: "0.8rem" }} placeholder="Votre message..." value={chatText} onChange={e => setChatText(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChatMessage()} data-testid="chat-input" />
                              <button className="btn btn-primary btn-sm" onClick={sendChatMessage}><Send size={14} /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : selectedCourse ? (
              /* Course purchase flow */
              <div data-testid="course-purchase-flow">
                <button className="back-btn" style={{ marginBottom: "0.5rem" }} onClick={() => setSelectedCourse(null)}>
                  <ChevronLeft size={16} /> Retour
                </button>
                <div className="card" style={{ padding: "1rem" }}>
                  {selectedCourse.image_url && <img src={selectedCourse.image_url} alt="" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 8, marginBottom: "0.75rem" }} />}
                  <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.25rem" }}>{selectedCourse.title}</h3>
                  <p style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "0.5rem" }}>{selectedCourse.description}</p>
                  <div style={{ fontSize: "0.75rem", color: "#475569", marginBottom: "0.75rem", display: "flex", gap: "0.75rem" }}>
                    <span><Video size={12} /> {selectedCourse.video_count} videos</span>
                    <span><FileText size={12} /> {selectedCourse.doc_count} documents</span>
                  </div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#2563EB", marginBottom: "0.75rem" }}>{formatUSD(selectedCourse.price || 0, exchangeRate)}</div>
                  
                  {/* QR Payment */}
                  <div style={{ marginBottom: "0.75rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                      <button className={`plan-card ${coursePaymentMethod === "qr" ? "active" : ""}`} style={{ flex: 1, padding: "0.5rem", textAlign: "center" }} onClick={() => setCoursePaymentMethod("qr")}>QR Code</button>
                      <button className={`plan-card ${coursePaymentMethod === "phone" ? "active" : ""}`} style={{ flex: 1, padding: "0.5rem", textAlign: "center" }} onClick={() => setCoursePaymentMethod("phone")}>Telephone</button>
                    </div>
                    {coursePaymentMethod === "qr" && (
                      <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto", paddingBottom: "0.5rem" }}>
                        {QR_CODES.map((qr, i) => (
                          <div key={qr.number || qr.label || `qr-${i}`} style={{ minWidth: 140, textAlign: "center", background: "#F8FAFC", borderRadius: 8, padding: "0.5rem" }}>
                            <img src={qr.image} alt={qr.label} style={{ width: 100, height: 100, objectFit: "contain", borderRadius: 6 }} />
                            <div style={{ fontSize: "0.7rem", fontWeight: 600, marginTop: "0.25rem" }}>{qr.label}</div>
                            <div style={{ fontSize: "0.65rem", color: "#64748B" }}>{qr.number}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {coursePaymentMethod === "phone" && (
                      <div style={{ textAlign: "center", padding: "0.5rem", background: "#F8FAFC", borderRadius: 8 }}>
                        <p style={{ fontSize: "0.8rem" }}>Envoyez le paiement au:</p>
                        <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#2563EB" }}>{PAYMENT_PHONE}</div>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: "0.5rem" }}>
                    <label style={{ fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>ID de Transaction</label>
                    <input className="form-input" placeholder="Entrez l'ID de transaction" value={courseTransactionId} onChange={e => setCourseTransactionId(e.target.value)} data-testid="course-transaction-id" />
                  </div>
                  <button className="btn btn-primary btn-full" onClick={handlePurchaseCourse} disabled={buyingCourse || !courseTransactionId.trim()} data-testid="submit-course-purchase">
                    <DollarSign size={16} /> {buyingCourse ? "Envoi..." : "Acheter la formation"}
                  </button>
                </div>
              </div>
            ) : (
              /* Courses catalog */
              <>
                {/* Certificate notifications */}
                {certNotifications.filter(n => !n.read).length > 0 && (
                  <div style={{ marginBottom: "0.75rem" }}>
                    {certNotifications.filter(n => !n.read).map(n => (
                      <div key={n.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0.75rem", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, marginBottom: "0.3rem" }} data-testid={`cert-notif-${n.id}`}>
                        <Award size={16} color="#16A34A" />
                        <div style={{ flex: 1, fontSize: "0.8rem" }}>
                          <div style={{ fontWeight: 600, color: "#16A34A" }}>{n.title}</div>
                          <div style={{ color: "#475569", fontSize: "0.75rem" }}>{n.message}</div>
                        </div>
                        {n.certificate_filename && (
                          <a href={`${API}/view/document/${n.certificate_filename}?token=${localStorage.getItem("client_token")}`} target="_blank" rel="noopener noreferrer"
                            className="btn btn-success btn-sm" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.7rem" }} data-testid={`download-cert-${n.id}`}>
                            <Download size={12} /> PDF
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* My purchased courses */}
                {myCourses.length > 0 && (
                  <div style={{ marginBottom: "1rem" }}>
                    <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#1E293B", marginBottom: "0.5rem" }}>Mes Formations</h3>
                    {myCourses.map(c => {
                      const cert = getCertForCourse(c.id);
                      return (
                        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem", background: c.status === "validated" ? "#F0FDF4" : "#FFFBEB", borderRadius: 8, marginBottom: "0.3rem", border: `1px solid ${c.status === "validated" ? "#BBF7D0" : "#FDE68A"}` }} data-testid={`my-course-${c.id}`}>
                          {c.image_url && <img src={c.image_url} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{c.title}</div>
                            <div style={{ fontSize: "0.7rem", color: c.status === "validated" ? "#16A34A" : "#D97706" }}>
                              {c.status === "validated" ? "Acces accorde" : c.status === "pending" ? "En attente de validation" : "Refuse"}
                            </div>
                            {cert && (
                              <a href={`${API}/view/document/${cert.certificate_filename}?token=${localStorage.getItem("client_token")}`} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: "0.7rem", color: "#16A34A", display: "inline-flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                                <Award size={11} /> Certificat disponible
                              </a>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: "0.25rem" }}>
                            {cert && (
                              <a href={`${API}/view/document/${cert.certificate_filename}?token=${localStorage.getItem("client_token")}`} target="_blank" rel="noopener noreferrer"
                                className="btn btn-success btn-sm" style={{ textDecoration: "none", padding: "4px 8px" }} data-testid={`cert-download-${c.id}`}>
                                <Download size={13} />
                              </a>
                            )}
                            {c.status === "validated" && (
                              <button className="btn btn-primary btn-sm" onClick={() => setViewingCourse(c)} data-testid={`view-course-${c.id}`}>
                                <Eye size={13} /> Voir
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Available courses */}
                <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "#1E293B", marginBottom: "0.5rem" }}>Formations disponibles</h3>
                {courses.length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={32} className="empty-icon" />
                    <p>Aucune formation disponible</p>
                  </div>
                ) : (
                  courses.map(course => {
                    const purchased = isCoursePurchased(course.id);
                    return (
                      <div key={course.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem", background: "#fff", borderRadius: 10, marginBottom: "0.5rem", border: "1px solid #E2E8F0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }} data-testid={`course-${course.id}`}>
                        {course.image_url ? (
                          <img src={course.image_url} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: 60, height: 60, borderRadius: 8, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <BookOpen size={24} color="#2563EB" />
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B" }}>{course.title}</div>
                          {course.professor_name && <div style={{ fontSize: "0.7rem", color: "#2563EB", marginTop: 1 }}><User size={10} /> {course.professor_name}</div>}
                          {course.description && <div style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 2 }}>{course.description.substring(0, 60)}{course.description.length > 60 ? "..." : ""}</div>}
                          <div style={{ display: "flex", gap: "0.5rem", fontSize: "0.7rem", color: "#475569", marginTop: 4 }}>
                            <span><Video size={11} /> {course.video_count} videos</span>
                            <span><FileText size={11} /> {course.doc_count} docs</span>
                            {course.avg_rating > 0 && <span style={{ color: "#F59E0B" }}><Star size={11} /> {course.avg_rating} ({course.review_count})</span>}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontWeight: 800, color: "#2563EB", fontSize: "0.95rem" }}>{formatUSD(course.price || 0, exchangeRate)}</div>
                          {purchased ? (
                            <span style={{ fontSize: "0.7rem", color: purchased.status === "validated" ? "#16A34A" : "#D97706" }}>
                              {purchased.status === "validated" ? "Achete" : "En attente"}
                            </span>
                          ) : (
                            <button className="btn btn-primary btn-sm" style={{ marginTop: 4, fontSize: "0.7rem" }} onClick={() => setSelectedCourse(course)} data-testid={`buy-course-${course.id}`}>
                              Acheter
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
        )}

        {/* Subscriptions View */}
        {activeView === "subscriptions" && (
          <div data-testid="client-subscriptions-view">
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <CalendarDays size={18} /> Mes Abonnements
            </h3>
            {mySubscriptions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem 1rem", color: "#94A3B8" }}>
                <CalendarDays size={32} style={{ margin: "0 auto 0.5rem", display: "block", opacity: 0.5 }} />
                <div style={{ fontSize: "0.85rem" }}>Aucun abonnement actif</div>
                <div style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>Commandez un service pour activer votre abonnement</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {mySubscriptions.map((sub) => {
                  const isExpired = sub.status === "expired";
                  const endDate = new Date(sub.end_date);
                  const now = new Date();
                  const daysLeft = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
                  const hoursLeft = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60)));
                  const timeDisplay = daysLeft > 0 ? `${daysLeft}j restants` : hoursLeft > 0 ? `${hoursLeft}h restants` : "Expiré";
                  return (
                    <div key={sub.id} data-testid={`client-sub-${sub.id}`} style={{
                      padding: "0.75rem",
                      borderRadius: "10px",
                      background: isExpired ? "#FFF5F5" : "linear-gradient(135deg, #F0FDF4, #ECFDF5)",
                      border: `1px solid ${isExpired ? "#FECACA" : "#BBF7D0"}`,
                      position: "relative",
                      overflow: "hidden"
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E293B" }}>{sub.service_name}</div>
                          <div style={{ fontSize: "0.78rem", color: "#475569" }}>{sub.plan_name} - {formatUSD(sub.price, exchangeRate)}</div>
                        </div>
                        <div style={{
                          padding: "0.2rem 0.6rem",
                          borderRadius: "999px",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          background: isExpired ? "#FEE2E2" : "#DCFCE7",
                          color: isExpired ? "#DC2626" : "#16A34A",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem"
                        }}>
                          {isExpired ? <Timer size={12} /> : <CheckCircle size={12} />}
                          {isExpired ? "Expiré" : timeDisplay}
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.4rem", fontSize: "0.7rem", color: "#94A3B8" }}>
                        <span>Début: {new Date(sub.start_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        <span>Fin: {new Date(sub.end_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      </div>
                      {!isExpired && (
                        <div style={{ marginTop: "0.4rem", height: "4px", background: "#E2E8F0", borderRadius: "2px", overflow: "hidden" }}>
                          <div style={{
                            height: "100%",
                            background: daysLeft <= 1 ? "#F59E0B" : "#16A34A",
                            borderRadius: "2px",
                            width: `${Math.max(5, Math.min(100, ((sub.duration_days - daysLeft) / sub.duration_days) * 100))}%`,
                            transition: "width 0.3s"
                          }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Crypto Exchange View */}
        {activeView === "crypto" && (
          <CryptoExchangeUser apiCall={apiCall} API={API} tokenKey="client_token" role="client" />
        )}
        {activeView === "balance" && (
          <ClientBalanceView apiCall={apiCall} API={API} />
        )}
        {activeView === "p2p" && (
          <P2PMarketplace clientUser={clientUser} onBack={() => setActiveView("services")} />
        )}
        {activeView === "game-topup" && (
          <ClientGameTopup
            clientBalanceHtg={clientBalanceHtg}
            exchangeRate={exchangeRate}
            onBalanceChange={(newBalance) => setClientBalanceHtg(newBalance)}
          />
        )}
        {activeView === "smm" && (
          <ClientSMM
            apiCall={apiCall}
            clientBalanceHtg={clientBalanceHtg}
            exchangeRate={exchangeRate}
            onBalanceChange={(newBalance) => setClientBalanceHtg(newBalance)}
          />
        )}
      </div>
      </div>{/* /.sidebar-content */}
      <ClientChatWidget />
    </div>
  );
};

export default ClientDashboard;
